import React from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { closeModal } from 'features/utilsSlice';
import { formatLargeNumber, getGigOwner } from 'utils/helpers';
import useModalOpen from 'hooks/useModalOpen';
import useIsInfluencer from 'hooks/useIsInfluencer';
import DownloadIcon from 'public/icons/download-green-circle.svg';
import EyeIcon from 'public/icons/eye.svg';
import TransitionDialog from 'components/TransitionDialog';
import BottomButtons from 'components/BottomButtons';
import DisplayAsset from 'components/DisplayAsset';
import useProfileIds from 'hooks/useProfileIds';
import FullScreenLoader from './FullScreenLoader';
import uploadViewsStyles from 'styles/components/milestones/UploadViewsDialog.module.scss';
import styles from 'styles/components/milestones/Milestone.module.scss';

const size = { width: 100, height: 100 };

const DialogContent = ({ handleClick, handleClose, disabled, milestone, progress }) => {
  const router = useRouter();
  const isInfluencer = useIsInfluencer();
  const [screenshot, name] = useSelector(state => {
    const { milestone, screenshot } = state.utils.modalData['Full Screenshot'];
    const gig = state.gigs.mapping[milestone.gig_id];
    const currentMilestone =
      state.utils.modalData['New Milestone']?.milestone ||
      gig.milestones.find(m => m.id === milestone.id);

    return [currentMilestone[screenshot], screenshot];
  });

  const profileIds = useProfileIds();
  const mappingGig = useSelector(state => state.gigs.mapping[milestone.gig_id]);
  const modalGig = useSelector(state => state.utils.modalData['New Milestone']?.gig);
  const gig = mappingGig || modalGig;
  const currentUser = useSelector(state => state.auth.currentUser);
  const gigOwner = getGigOwner(currentUser, gig.user_id);
  const hasLicenseMedia = milestone.inventory_space.license && milestone.inventory_space.has_media;
  const isInfluencerAssigned = profileIds.includes(gig.profile_id);
  const hasContent = milestone.inventory_space.has_content;
  const hasDownloadIcon = !isInfluencer && gigOwner && !hasLicenseMedia && !hasContent;

  if (!screenshot || !name) return null;

  return (
    <>
      <div className={styles.container}>
        {milestone.inventory_space.has_content ? (
          <div className={styles.assetContainer}>
            <DisplayAsset asset={milestone.content} size={size} whiteIcon />
          </div>
        ) : (
          <div className={styles.assetContainer}>
            <Image layout="fill" src={screenshot?.url} alt="Screenshot" unoptimized />
          </div>
        )}

        <div className={styles.assetInfo}>
          <div
            className={styles.row}
            style={
              hasDownloadIcon
                ? { display: 'flex', justifyContent: 'space-between', width: '100%' }
                : {}
            }
          >
            <div>
              <h3>Asset:</h3>

              <span className={styles.name} style={{ display: 'block' }}>
                {milestone.content.name || screenshot.name}
              </span>
            </div>

            {hasDownloadIcon && (
              <Link href={screenshot.download_url} passHref>
                <div className={styles.downloadIcon}>
                  <DownloadIcon />
                </div>
              </Link>
            )}
          </div>

          <div className={styles.row}>
            <div>
              <h3>Size:</h3>
              <span>{milestone.content.file_size || screenshot.file_size}</span>
            </div>
          </div>
        </div>
      </div>

      {name === 'views_screenshot' && milestone.views > 0 && (
        <div
          className={uploadViewsStyles.content}
          style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
        >
          <div className={uploadViewsStyles.views}>
            <EyeIcon width={35} height={35} style={{ position: 'absolute', left: 10 }} />
            <h2>{formatLargeNumber(milestone.views)} views</h2>
          </div>
        </div>
      )}

      {isInfluencerAssigned && !milestone.guaranteed_gains ? (
        <BottomButtons
          text="Close"
          textContained="Replace"
          outlined={milestone.status === 'approved'}
          handleClose={handleClose}
          handleClick={() => handleClick(name)}
          disabled={disabled}
        />
      ) : (
        <BottomButtons
          text="Close"
          textContained="Download"
          handleClick={() =>
            router.push(
              milestone.delivery_screenshot?.download_url || milestone.content.download_url
            )
          }
          handleClose={handleClose}
          outlined={gigOwner && (hasLicenseMedia || hasContent) ? false : true}
        />
      )}

      {progress < 100 && <FullScreenLoader progress={progress} />}
    </>
  );
};

const FullScreenshotDialog = props => {
  const dispatch = useDispatch();
  const open = useModalOpen('Full Screenshot');

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick') return;
    dispatch(closeModal('Full Screenshot'));
  };

  return (
    <TransitionDialog open={open} handleClose={handleClose} noCloseIcon>
      <DialogContent {...props} handleClose={handleClose} />
    </TransitionDialog>
  );
};

DialogContent.propTypes = {
  handleClick: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  milestone: PropTypes.object.isRequired,
  progress: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
};

DialogContent.defaultProps = {
  disabled: false,
};

export default FullScreenshotDialog;
